import React, { useState, useEffect } from 'react';
import api from '../api';
import { PageHeader, Card, Button, LoadingSpinner } from '../components/UI';
import * as I from 'lucide-react';

const TdsPaidBatchInvoicesPage = ({ batch, onBack }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [vendorMap, setVendorMap] = useState({});

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!batch || !batch.id) {
        setError('Batch id is missing; unable to load invoices.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const resp = await api.get(`/invoices/by-tds-batch/${batch.id}`);
        const data = Array.isArray(resp.data) ? resp.data : [];
        setInvoices(data);
        if (!data.length) setError('No invoices found for this batch.');
      } catch (err) {
        console.error('Error loading invoices for batch', err);
        const msg = err?.response?.data?.message || 'Failed to load invoice details for this batch.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [batch]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const missingVendorIds = [
          ...new Set(
            invoices
              .map((inv) => inv.vendorId)
              .filter((id) => id != null && !vendorMap[id])
          ),
        ];
        if (!missingVendorIds.length) return;

        const responses = await Promise.all(
          missingVendorIds.map((id) =>
            api
              .get(`/vendors/${id}`)
              .then((res) => ({ id, name: res.data?.name || `Vendor ${id}` }))
              .catch(() => ({ id, name: `Vendor ${id}` })),
          ),
        );

        const newMap = {};
        responses.forEach(({ id, name }) => {
          newMap[id] = name;
        });
        if (Object.keys(newMap).length) {
          setVendorMap((prev) => ({ ...prev, ...newMap }));
        }
      } catch (e) {
        console.error('Failed to fetch vendors for batch invoices', e);
      }
    };

    if (invoices.length) {
      fetchVendors();
    }
  }, [invoices, vendorMap]);

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(Number(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return 'Invalid Date';
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const filteredInvoices = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return invoices.filter((inv) => {
      if (!term) return true;

      const vendorNameResolved =
        inv.vendorName ||
        (inv.vendor && inv.vendor.name) ||
        (inv.vendorId != null ? vendorMap[inv.vendorId] : '') ||
        '';

      const vendorName = vendorNameResolved.toLowerCase();
      const invoiceNumber = (inv.invoiceNumber || '').toString().toLowerCase();

      return (
        (invoiceNumber && invoiceNumber.includes(term)) ||
        (vendorName && vendorName.includes(term))
      );
    });
  }, [invoices, searchTerm, vendorMap]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 bg-gray-50 min-h-screen">
      <PageHeader
        title="Paid Batch Invoices"
        subtitle={batch ? `Batch: ${batch.batchNumber || batch.id}` : 'No batch selected'}
        actions={[
          <Button key="back" variant="outline" onClick={onBack}>
            <I.ArrowLeft className="w-4 h-4 mr-2" />
            Back to Paid Batches
          </Button>,
        ]}
      />

      <Card>
        {/* Batch Summary */}
        {batch && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">Batch Summary</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-gray-600">Total TDS:</span>
                <div className="font-semibold text-gray-800">{formatCurrency(batch.totalTdsAmount || 0)}</div>
              </div>
              <div>
                <span className="text-gray-600">Total Interest:</span>
                <div className="font-semibold text-gray-800">{formatCurrency(batch.totalInterest || 0)}</div>
              </div>
              <div>
                <span className="text-gray-600">Final Amount:</span>
                <div className="font-semibold text-blue-600">{formatCurrency(batch.finalTdsAmount || 0)}</div>
              </div>
              <div>
                <span className="text-gray-600">Invoices:</span>
                <div className="font-semibold text-gray-800">{batch.invoiceCount || 0}</div>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex flex-col gap-3 mb-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-medium">Invoices in Batch</h3>
            <div className="flex items-center gap-2 text-xs">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by invoice # or vendor..."
                className="border border-gray-300 rounded px-2 py-1 text-xs w-48"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
            <span className="ml-3">Loading invoices...</span>
          </div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-sm text-gray-500">No invoices found for this batch.</div>
        ) : (
          <div className="overflow-auto text-xs">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    <div className="sticky left-0 bg-gray-50 z-10 pl-2 pr-4 -ml-2">Invoice #</div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date Recvd</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor Name</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">TDS Amount</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Interest</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Final TDS</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Invoice Paid</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map((inv) => {
                  const vendorName =
                    inv.vendorName ||
                    (inv.vendor && inv.vendor.name) ||
                    (inv.vendorId != null ? vendorMap[inv.vendorId] : `Vendor ${inv.vendorId || ''}`);
                  const tdsAmt = inv.tdsAmount != null ? inv.tdsAmount : 0;
                  const tdsPerc = inv.tdsPercentage != null ? inv.tdsPercentage : 0;
                  const invoicePayable = inv.invoicePayable != null ? inv.invoicePayable : 0;

                  return (
                    <tr key={inv.id || inv.invoiceNumber} className="text-xs">
                      <td className="px-4 py-1 text-gray-700">
                        <div className="sticky left-0 bg-white z-10 pl-2 pr-4 -ml-2">
                          {inv.invoiceNumber || inv.id || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-1 text-gray-700">
                        {inv.dateReceived || inv.receivedDateTime ? formatDate(inv.dateReceived || inv.receivedDateTime) : 'N/A'}
                      </td>
                      <td className="px-4 py-1 text-gray-700">
                        {inv.invoiceDate ? formatDate(inv.invoiceDate) : 'N/A'}
                      </td>
                      <td className="px-4 py-1 text-gray-700">
                        <div className="min-w-[150px] max-w-[250px] break-words whitespace-normal">{vendorName}</div>
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {inv.tax != null ? formatCurrency(inv.tax) : 'N/A'}
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {inv.totalAmount != null ? formatCurrency(inv.totalAmount) : 'N/A'}
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {tdsAmt != null
                          ? `${formatCurrency(tdsAmt)}${tdsPerc ? ` (${tdsPerc}%)` : ''}`
                          : 'N/A'}
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {formatCurrency(inv.tdsLatePaymentInterest || 0)}
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {formatCurrency((tdsAmt || 0) + (inv.tdsLatePaymentInterest || 0))}
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {formatCurrency(invoicePayable)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TdsPaidBatchInvoicesPage;
